import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDownRight } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { LineReveal } from "./anim";
import { CONTACT, IMAGES } from "./config";
import { openReservation } from "./ReservationDialog";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.85]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Berline noire de luxe MN VTC au coucher de soleil" className="h-full w-full object-cover object-center" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-ink" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />

      <motion.div style={{ y: contentY }} className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-20 lg:px-10 lg:pb-28">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 1 }}
          className="mb-6 text-xs uppercase tracking-[0.35em] text-rose"
        >
          Voiture de transport avec chauffeur — {CONTACT.zone}
        </motion.p>

        <h1 className="font-serif leading-[0.92] tracking-tight text-5xl sm:text-7xl lg:text-[7.5rem] text-blush">
          <LineReveal lines={["Le trajet devient", "une expérience."]} delay={0.35} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }}
          className="mt-8 max-w-md font-serif italic text-2xl sm:text-3xl text-blush/70"
        >
          {CONTACT.tagline}.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button data-testid="hero-whatsapp-cta" onClick={openReservation}
            className="group inline-flex items-center gap-3 rounded-full bg-blush px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-ink transition-transform hover:scale-[1.04]">
            <FaWhatsapp size={18} /> Réserver sur WhatsApp
          </button>
          <a data-testid="hero-call-cta" href={CONTACT.phoneHref}
            className="inline-flex items-center gap-3 rounded-full border border-rose/50 px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-rose transition-colors hover:bg-rose/10">
            <Phone size={16} /> {CONTACT.phoneDisplay}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-6 z-10 hidden items-center gap-2 text-xs uppercase tracking-[0.2em] text-blush/60 lg:flex"
      >
        Défiler <ArrowDownRight size={16} />
      </motion.div>
    </section>
  );
};

export default Hero;
